import { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FiArrowLeft, FiEye, FiCheck, FiX } from 'react-icons/fi';

import { questionsApi } from '@/services/questions';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/custom/loader';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHeaderWrapper,
  TableRow
} from '@/components/ui/table';

const MODALITY_LABEL = {
  multiple_choice: 'Múltipla escolha',
  discursive:      'Discursiva',
  essay:           'Redação',
  true_false:      'V/F',
  short_answer:    'Curta'
};

const ETAPA_LABEL = {
  serie_3_em: '3ª EM',
  ano_9_ef:   '9º EF',
  ano_5_ef:   '5º EF',
  ano_2_ef:   '2º EF'
};

const TABLE_HEAD = [
  { label: 'Código', width: 120 },
  { label: 'Tipo', width: 130 },
  { label: 'Etapa', width: 80 },
  { label: 'Matéria', width: 160 },
  { label: 'Comando' },
  { label: 'Ações', width: 90 }
];

function truncate(text = '', n = 110) {
  const clean = String(text).replace(/<[^>]+>/g, '').trim();
  return clean.length > n ? `${clean.slice(0, n - 1)}…` : clean;
}

function buildIndex(included = []) {
  const idx = {};
  included.forEach(item => {
    idx[`${item.type}:${item.id}`] = item;
  });
  return idx;
}

function getRelated(question, relName, includedIdx) {
  const rel = question.relationships?.[relName]?.data;
  if (!rel) return [];
  const arr = Array.isArray(rel) ? rel : [rel];
  return arr.map(r => includedIdx[`${r.type}:${r.id}`]).filter(Boolean);
}

function Section({ title, delay = 0, children }) {
  return (
    <section
      className="opacity-0"
      style={{
        animation: `section-in 320ms cubic-bezier(0.23, 1, 0.32, 1) ${delay}ms forwards`
      }}
    >
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-light">
        {title}
      </h4>
      {children}
    </section>
  );
}

function TagRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span className="min-w-[140px] text-xs text-neutral-light">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

// Inject keyframes once
if (typeof window !== 'undefined' && !document.getElementById('section-in-kf')) {
  const s = document.createElement('style');
  s.id = 'section-in-kf';
  s.textContent = `
    @keyframes section-in {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      @keyframes section-in { from { opacity: 0; } to { opacity: 1; } }
    }
  `;
  document.head.appendChild(s);
}

export default function QuestionsList({ filters, onBack }) {
  const [questions, setQuestions] = useState([]);
  const [included, setIncluded] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({});
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    questionsApi.index({ filters, page: 1, perPage: 25 })
      .then(json => {
        const data = json?.data || [];
        setQuestions(data);
        setIncluded(buildIndex(json?.included || []));
        setMeta(json?.meta || {});
        setHasMore(data.length >= 25);
      })
      .catch(() => { setQuestions([]); setIncluded({}); })
      .finally(() => setLoading(false));
  }, [filters]);

  const loadMore = () => {
    const next = page + 1;
    questionsApi.index({ filters, page: next, perPage: 25 }).then(json => {
      const items = json?.data || [];
      setQuestions(prev => [...prev, ...items]);
      setIncluded(prev => ({ ...prev, ...buildIndex(json?.included || []) }));
      setHasMore(items.length >= 25);
      setPage(next);
    });
  };

  const openQuestion = questions.find(q => q.id === openId);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={onBack}>
          <ButtonIcon><FiArrowLeft /></ButtonIcon>
          Ajustar filtros
        </Button>
        <span className="text-xs text-neutral">
          {questions.length} de {meta.total_count ?? '?'} questões
        </span>
      </div>

      {loading ? (
        <Loader />
      ) : questions.length === 0 ? (
        <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">
          Nenhuma questão bate com os filtros.
        </div>
      ) : (
        <InfiniteScroll
          dataLength={questions.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<Loader />}
          className="infinite-scroll__full"
        >
          <Table className="min-w-[900px]">
            <TableHeaderWrapper tableHead={TABLE_HEAD} />
            <TableBody>
              {questions.map(q => {
                const a = q.attributes || {};
                const taxs = a.taxonomies || [];
                const materia = taxs.find(t =>
                  ['Matemática', 'Língua Portuguesa', 'Lingua Portuguesa'].includes(t.name)
                )?.name || '—';
                return (
                  <TableRow key={q.id}>
                    <TableCell className="font-mono text-xs text-neutral">
                      {a['external-id'] || a.code || `#${q.id}`}
                    </TableCell>
                    <TableCell>
                      <Badge variant="neutral-lightest" size="sm">
                        {MODALITY_LABEL[a.modality] || a.modality || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {a.etapa ? (
                        <Badge variant="default" size="sm">{ETAPA_LABEL[a.etapa] || a.etapa}</Badge>
                      ) : (
                        <span className="text-xs text-neutral-light">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{materia}</TableCell>
                    <TableCell className="text-sm">{truncate(a.description, 100)}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="xs" onClick={() => setOpenId(q.id)}>
                        <ButtonIcon><FiEye /></ButtonIcon>
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      )}

      {/* Preview sheet */}
      <Sheet open={!!openId} onOpenChange={open => !open && setOpenId(null)}>
        <SheetContent
          side="right"
          className="flex w-full max-w-2xl flex-col gap-0 overflow-hidden p-0"
        >
          {openQuestion && (() => {
            const a = openQuestion.attributes || {};
            const taxs = a.taxonomies || [];
            const alts = a.alternatives || [];

            // Particiona taxonomies por tipo (heurística por nome/padrão)
            const SAEB_CODE = /^(D\d+|[259][A-Z]+\d+\.\d+|3[A-Z]+\d+\.\d+)/;
            const MATERIAS = new Set(['Matemática', 'Língua Portuguesa', 'Lingua Portuguesa']);
            const materia = taxs.find(t => MATERIAS.has(t.name))?.name;
            const descritorSaeb = taxs.find(t => SAEB_CODE.test(t.name))?.name;
            const subConteudo = taxs.filter(t => !MATERIAS.has(t.name) && !SAEB_CODE.test(t.name));

            const intro = getRelated(openQuestion, 'introduction', included)[0];
            const enunciado = intro?.attributes?.body;
            const codigo = a['external-id'] || a.code || `#${openQuestion.id}`;

            return (
              <>
                {/* Sticky header */}
                <div className="flex-shrink-0 border-b border-neutral-lightest bg-white px-6 py-4">
                  <SheetHeader className="space-y-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5">
                        <SheetTitle className="font-mono text-sm tracking-wide text-foreground">
                          {codigo}
                        </SheetTitle>
                        <Badge variant="neutral-lightest" size="sm">
                          {MODALITY_LABEL[a.modality] || a.modality || '—'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-neutral">
                      {a.etapa && <span>{ETAPA_LABEL[a.etapa] || a.etapa}</span>}
                      {a.etapa && materia && <span className="text-neutral-light">·</span>}
                      {materia && <span>{materia}</span>}
                      {(a.etapa || materia) && descritorSaeb && <span className="text-neutral-light">·</span>}
                      {descritorSaeb && <span className="font-mono">{descritorSaeb}</span>}
                    </div>
                  </SheetHeader>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <div className="space-y-6">
                    {enunciado && (
                      <Section title="Enunciado" delay={0}>
                        <div
                          className="prose prose-sm max-w-none leading-relaxed text-foreground"
                          dangerouslySetInnerHTML={{ __html: enunciado }}
                        />
                      </Section>
                    )}

                    {a.description && (
                      <Section title="Comando" delay={60}>
                        <p className="border-l-2 border-primary/60 bg-primary/[0.03] py-2 pl-4 pr-2 text-sm leading-relaxed text-foreground">
                          {a.description}
                        </p>
                      </Section>
                    )}

                    {alts.length > 0 && (
                      <Section title="Alternativas" delay={120}>
                        <ol className="space-y-2">
                          {alts.map((alt, i) => {
                            const letter = String.fromCharCode(65 + i); // A, B, C...
                            const correct = alt['is-correct'];
                            return (
                              <li
                                key={alt.id}
                                className={[
                                  'flex items-start gap-3 rounded-lg border p-3 transition-colors duration-150',
                                  correct
                                    ? 'border-success/30 bg-success/[0.04] ring-1 ring-success/15'
                                    : 'border-neutral-lightest bg-white'
                                ].join(' ')}
                              >
                                <span
                                  className={[
                                    'flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold',
                                    correct
                                      ? 'bg-success/15 text-success-dark'
                                      : 'bg-neutral-lightest text-neutral'
                                  ].join(' ')}
                                >
                                  {letter}
                                </span>
                                <div className="flex-1 text-sm leading-relaxed">
                                  <div dangerouslySetInnerHTML={{ __html: alt.description || '' }} />
                                </div>
                                {correct && (
                                  <FiCheck className="size-5 shrink-0 text-success-dark" strokeWidth={2.5} />
                                )}
                              </li>
                            );
                          })}
                        </ol>
                      </Section>
                    )}

                    {(materia || a.etapa || descritorSaeb || subConteudo.length > 0) && (
                      <Section title="Classificação" delay={180}>
                        <div className="space-y-3">
                          {materia && (
                            <TagRow label="Matéria">
                              <Badge variant="default" size="sm">{materia}</Badge>
                            </TagRow>
                          )}
                          {a.etapa && (
                            <TagRow label="Etapa">
                              <Badge variant="neutral-lightest" size="sm">
                                {ETAPA_LABEL[a.etapa] || a.etapa}
                              </Badge>
                            </TagRow>
                          )}
                          {descritorSaeb && (
                            <TagRow label="Descritor SAEB">
                              <Badge variant="neutral-lightest" size="sm">
                                <span className="font-mono text-xs">{descritorSaeb}</span>
                              </Badge>
                            </TagRow>
                          )}
                          {subConteudo.length > 0 && (
                            <TagRow label="Sub-conteúdos Ementa">
                              <div className="flex flex-wrap gap-1.5">
                                {subConteudo.map(t => (
                                  <Badge key={t.id} variant="neutral-lightest" size="sm">{t.name}</Badge>
                                ))}
                              </div>
                            </TagRow>
                          )}
                        </div>
                      </Section>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 border-t border-neutral-lightest bg-white px-6 py-3">
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenId(null)}
                      className="active:scale-[0.97] transition-transform duration-150"
                      style={{ transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)' }}
                    >
                      Fechar
                    </Button>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
